import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Import pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Leads from "./pages/Leads";
import PickPool from "./pages/PickPool";
import LeadDetail from "./pages/LeadDetail";
import CallHistory from "./pages/CallHistory";
import FollowUps from "./pages/FollowUps";
import Samples from "./pages/Samples";
import Orders from "./pages/Orders";
import Users from "./pages/Users";
import Reports from "./pages/Reports";
import Backup from "./pages/Backup";
import LeadManagement from "./pages/LeadManagement";
import SmartAllocation from "./pages/SmartAllocation";
import CRMSelector from "./pages/CRMSelector";
import Sidebar from "./components/Sidebar";
import AttendanceGate, { AttendanceBadge } from "./components/AttendanceGate";

// Export CRM pages
import ExportDashboard from "./pages/export/ExportDashboard";
import BdManagerReview from "./pages/BdManagerReview";
import ExportImports from "./pages/export/ExportImports";
import ExportFactories from "./pages/export/ExportFactories";
import ExportFactoryDetail from "./pages/export/ExportFactoryDetail";
import ExportPipeline from "./pages/export/ExportPipeline";
import ExportTemplates from "./pages/export/ExportTemplates";
import ExportProducts from "./pages/export/ExportProducts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export { BACKEND_URL };
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Axios interceptor for auth
let interceptorId = null;

const setupAxiosInterceptors = (token, logout) => {
  axios.defaults.headers.common['Authorization'] = token ? `Bearer ${token}` : '';
  
  // Remove existing interceptor if any
  if (interceptorId !== null) {
    axios.interceptors.response.eject(interceptorId);
  }
  
  interceptorId = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        logout();
        toast.error("Session expired. Please login again.");
      }
      return Promise.reject(error);
    }
  );
};

// Login Redirect Component - Handles redirect when user is already logged in
const LoginRedirect = () => {
  const location = useLocation();
  const { user } = useAuth();

  // Logistics users go straight to /samples
  if (user?.role === 'logistics') {
    return <Navigate to="/samples" replace />;
  }

  // Check if there's an intended destination
  const from = location.state?.from?.pathname;
  
  if (from && from !== '/login' && from !== '/') {
    // If user was trying to access an export route, update localStorage
    if (from.startsWith('/export')) {
      localStorage.setItem('crm_type', 'export');
    }
    return <Navigate to={from} replace />;
  }
  
  // Default: check saved CRM preference
  const savedCRM = localStorage.getItem('crm_type');
  if (savedCRM === 'export') {
    return <Navigate to="/export/dashboard" replace />;
  }
  
  return <Navigate to="/select-crm" replace />;
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  // Check if user is trying to access an Export CRM route
  const isExportRoute = location.pathname.startsWith('/export');
  
  // Update localStorage crm_type based on current route to prevent unwanted redirects
  useEffect(() => {
    if (user && isExportRoute) {
      localStorage.setItem('crm_type', 'export');
    } else if (user && !isExportRoute && location.pathname !== '/select-crm' && location.pathname !== '/login') {
      // Only set domestic if we're not on select-crm or login pages
      if (location.pathname !== '/' && !location.pathname.startsWith('/export')) {
        localStorage.setItem('crm_type', 'domestic');
      }
    }
  }, [user, isExportRoute, location.pathname]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !['admin', 'super_admin'].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Logistics role is locked to /samples. Any other authenticated route redirects.
  if (user.role === 'logistics' && location.pathname !== '/samples') {
    return <Navigate to="/samples" replace />;
  }

  return children;
};

// Main Layout with Sidebar
const MainLayout = ({ children, crmType = "domestic" }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <AttendanceGate>
      <div className="app-layout bg-slate-50 min-h-screen">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} crmType={crmType} />
        <main className="main-content md:ml-64">
          {/* Mobile header */}
          <div className="md:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-md hover:bg-slate-100"
              data-testid="mobile-menu-btn"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <span className="font-semibold text-slate-900">Locofast CRM</span>
            <div className="w-10" />
          </div>
          {/* Desktop attendance badge bar */}
          <div className="hidden md:flex sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 px-6 py-2 items-center justify-end gap-3">
            <AttendanceBadge />
          </div>
          <div className="p-6 md:p-8">
            {children}
          </div>
        </main>
      </div>
    </AttendanceGate>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('lf_token'));
  const [isLoading, setIsLoading] = useState(true);

  const login = (accessToken, userData) => {
    localStorage.setItem('lf_token', accessToken);
    setToken(accessToken);
    setUser(userData);
    setupAxiosInterceptors(accessToken, logout);
  };

  const logout = async () => {
    // Cleanly close any open active session so total_active_seconds is accurate
    try {
      await axios.post(`${API}/attendance/mark-inactive`, { source: "logout" });
    } catch (_) { /* ignore — token may already be invalid */ }
    localStorage.removeItem('lf_token');
    setToken(null);
    setUser(null);
    setupAxiosInterceptors(null, logout);
  };

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          setupAxiosInterceptors(token, logout);
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (error) {
          console.error("Token verification failed:", error);
          logout();
        }
      }
      setIsLoading(false);
    };

    verifyToken();
  }, [token]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isLoading }}>
      <div className="App">
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: 'Inter, sans-serif',
            },
          }}
        />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={
              user ? <LoginRedirect /> : <Login />
            } />
            
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <MainLayout>
                  <Dashboard />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/leads" element={
              <ProtectedRoute>
                <MainLayout>
                  <Leads />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/leads/:id" element={
              <ProtectedRoute>
                <MainLayout>
                  <LeadDetail />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/pick-pool" element={
              <ProtectedRoute>
                <MainLayout>
                  <PickPool />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/samples" element={
              <ProtectedRoute>
                <MainLayout>
                  <Samples />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/orders" element={
              <ProtectedRoute>
                <MainLayout>
                  <Orders />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/calls" element={
              <ProtectedRoute>
                <MainLayout>
                  <CallHistory />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/followups" element={
              <ProtectedRoute>
                <MainLayout>
                  <FollowUps />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/reports" element={
              <ProtectedRoute>
                <MainLayout>
                  <Reports />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/users" element={
              <ProtectedRoute adminOnly>
                <MainLayout>
                  <Users />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/lead-management" element={
              <ProtectedRoute adminOnly>
                <MainLayout>
                  <LeadManagement />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/smart-allocation" element={
              <ProtectedRoute adminOnly>
                <MainLayout>
                  <SmartAllocation />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/bd-manager" element={
              <ProtectedRoute adminOnly>
                <MainLayout>
                  <BdManagerReview />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/backup" element={
              <ProtectedRoute adminOnly>
                <MainLayout>
                  <Backup />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            {/* CRM Selector - Home Page */}
            <Route path="/select-crm" element={
              <ProtectedRoute>
                <CRMSelector />
              </ProtectedRoute>
            } />
            
            {/* Export CRM Routes */}
            <Route path="/export/dashboard" element={
              <ProtectedRoute>
                <MainLayout crmType="export">
                  <ExportDashboard />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/export/imports" element={
              <ProtectedRoute>
                <MainLayout crmType="export">
                  <ExportImports />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/export/factories" element={
              <ProtectedRoute>
                <MainLayout crmType="export">
                  <ExportFactories />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/export/factories/:id" element={
              <ProtectedRoute>
                <MainLayout crmType="export">
                  <ExportFactoryDetail />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/export/pipeline" element={
              <ProtectedRoute>
                <MainLayout crmType="export">
                  <ExportPipeline />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/export/templates" element={
              <ProtectedRoute>
                <MainLayout crmType="export">
                  <ExportTemplates />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/export/products" element={
              <ProtectedRoute>
                <MainLayout crmType="export">
                  <ExportProducts />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/" element={<Navigate to="/select-crm" replace />} />
            <Route path="*" element={<Navigate to="/select-crm" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthContext.Provider>
  );
}

export default App;
